import React from 'react';
import { Card, CardContent, Button } from '@lognai/synapse-design-system-v2';
import {
  Calendar,
  CheckCircle2,
  Pencil,
  ChevronRight,
  ChevronLeft,
  Link2 as LinkIcon,
} from '@lognai/synapse-design-system-v2/icons';
import { Link } from 'react-router-dom';
import { stripMentions } from '@lognai/plugin-sdk';
import { STATUS_LABELS, type Task, type TaskStatus } from '../../types/tasks';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { LabelChips } from './LabelChips';
import { Badge } from '@lognai/synapse-design-system-v2';
import { getDueState, DUE_TONE_CLASS } from '../../utils/due-date';
import { externalSourceName } from '../../utils/external-source';

const NEXT_STATUS: Partial<Record<TaskStatus, TaskStatus>> = {
  todo: 'in_progress',
  in_progress: 'done',
};
const PREV_STATUS: Partial<Record<TaskStatus, TaskStatus>> = {
  done: 'in_progress',
  in_progress: 'todo',
};
const NEXT_LABEL: Partial<Record<TaskStatus, string>> = {
  todo: 'Start',
  in_progress: 'Mark Done',
};

export interface TaskCardProps {
  task: Task;
  onEdit?: (task: Task) => void;
  onStatusChange?: (id: string, status: TaskStatus) => void;
  onOpen?: (task: Task) => void;
  isUpdating?: boolean;
  /** Whether the current user may edit this task (assignee, creator, or admin). */
  canManage?: boolean;
  /** Whether the current user may change status (assignee only; creator/admin if unassigned). */
  canChangeStatus?: boolean;
}


const initials = (name?: string) =>
  (name || '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onStatusChange,
  onOpen,
  isUpdating,
  canManage = true,
  canChangeStatus = true,
}) => {
  const next = NEXT_STATUS[task.status];
  const prev = PREV_STATUS[task.status];
  const dueState = getDueState(task);
  const sourceName = externalSourceName(task);

  return (
    <Card
      className={`group mb-3 border border-border/60 bg-card transition-all duration-150 hover:border-primary/40 ${
        task.closed ? 'opacity-60' : ''
      }`}
    >
      <CardContent className="p-4">
        <div
          className={onOpen ? 'cursor-pointer' : undefined}
          onClick={() => onOpen?.(task)}
        >
          <div className="flex justify-between items-start gap-2 mb-2">
            <div className="font-semibold text-[0.9rem] leading-snug tracking-tight">{task.title}</div>
            <TaskPriorityBadge priority={task.priority} />
          </div>

          {/*
            What the task is about.

            A task can be linked to a record — it is how the Tasks rail on a
            shipment or order page works, and linking is a visibility decision
            (anyone who can read the record can read the task). The detail page
            showed the link; the board did not, so from here a task's whole
            context was invisible (QA ledger TASK-003).

            Stops the click reaching the card's own open handler, so this goes
            to the record rather than to the task.
          */}
          {task.entityRef && (
            <Link
              to={task.entityRef.path}
              onClick={(e) => e.stopPropagation()}
              className="mb-2 inline-flex max-w-full items-center gap-1 text-xs text-primary hover:underline"
            >
              <LinkIcon className="h-3 w-3 shrink-0" />
              <span className="truncate">{task.entityRef.label}</span>
            </Link>
          )}

          {(task.closed || sourceName || task.labels.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
              {sourceName && (
                // Not this board's to move or close: the badge says whose it is.
                <Badge variant="outline" className="gap-1" title={`Managed in ${sourceName}`}>
                  Managed in {sourceName}
                </Badge>
              )}
              {task.closed && (
                /* Muted, not red: closed is a state, and the red collided with
                   HIGH priority sitting alongside it (QA ledger TASK-007). */
                <Badge variant="outline" className="gap-1 bg-muted text-muted-foreground border-border">
                  <CheckCircle2 className="h-3 w-3" />
                  Closed
                </Badge>
              )}
              {task.labels.length > 0 && <LabelChips labels={task.labels} />}
            </div>
          )}

          {task.description && (
            // The stored description carries mention MARKERS (`@[Ann Lee](user:abc-123)`).
            // Rendered raw, the card leaks a user id into the board. This is a clamped
            // two-line preview, so mentions are flattened to plain `@Ann Lee` rather than
            // rendered as chips — MentionText's spans would fight the line-clamp.
            <p className="text-[0.8rem] leading-relaxed text-muted-foreground mb-3 line-clamp-2">
              {stripMentions(task.description)}
            </p>
          )}
        </div>

        {dueState && (
          <div className={`flex items-center gap-1.5 text-xs mb-3 ${DUE_TONE_CLASS[dueState.tone]}`}>
            <Calendar className="h-3.5 w-3.5" />
            Due {dueState.label}
            {dueState.tag && (
              <span className="font-semibold uppercase tracking-wide text-[0.65rem]">· {dueState.tag}</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border/60">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-primary text-[10px] font-semibold shrink-0"
              title={task.assigneeName || 'Unassigned'}
            >
              {initials(task.assigneeName)}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {task.assigneeName || 'Unassigned'}
            </span>
          </div>
          {onEdit && canManage && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => onEdit(task)}
              aria-label="Edit task"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {onStatusChange && canChangeStatus && (prev || next) && (
          // Wraps rather than overflowing: buttons are `whitespace-nowrap`, so in
          // a narrow lane (the Workspace card's stage columns) two side by side
          // need more room than the row has and would spill over their
          // neighbours. Below ~14rem they stack instead, each full width.
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Both stage moves are neutral: they are frequent, reversible
                housekeeping, and "Back" on its own read as "go back a page"
                rather than "return this task to the previous stage". */}
            {prev && (
              <Button
                variant="outline"
                size="sm"
                className="min-w-0 flex-1 basis-28"
                disabled={isUpdating}
                title={`Move back to ${STATUS_LABELS[prev]}`}
                onClick={() => onStatusChange(task.id, prev)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Move back
              </Button>
            )}
            {next && (
              <Button
                variant="outline"
                size="sm"
                className="min-w-0 flex-1 basis-28"
                disabled={isUpdating}
                title={`Move to ${STATUS_LABELS[next]}`}
                onClick={() => onStatusChange(task.id, next)}
              >
                {NEXT_LABEL[task.status]}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
